import { useCallback, useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Refresh, Search, GetApp, Lock } from "@material-ui/icons";
import { api } from "../../services/apiService";
import { drilldownResult, analyticsResult, exportResult } from "./review-status-logic";
import Menu from "../menu/menu";
import "./review-status.scss";

const EXAM_TYPE = "ABSITE";
const REVIEW_TYPE = "IMAGE_CANDIDATE";

// One KPI tile. `tone` maps to a themed accent so the two status axes read at a glance.
function StatTile({ label, value, tone }) {
  return (
    <Paper className={`rs-tile rs-tile--${tone || "neutral"}`} elevation={0}>
      <div className="rs-tile-value">{value}</div>
      <div className="rs-tile-label">{label}</div>
    </Paper>
  );
}

function TileRow({ title, hint, children }) {
  return (
    <div className="rs-section">
      <div className="rs-section-head">
        <Typography variant="subtitle1" className="rs-section-title">{title}</Typography>
        {hint && <Typography variant="caption" className="rs-section-hint">{hint}</Typography>}
      </div>
      <div className="rs-tiles">{children}</div>
    </div>
  );
}

function fmtPct(x) {
  return `${Math.round((x || 0) * 100)}%`;
}
function hrs(x) {
  return `${(x || 0).toFixed(1)}h`;
}

// Read-only operator analytics. Own flag (analyticsEnabled); dark-safe (loads on
// mount, 404 → "not enabled", never an error). Reads only — throughput, review
// latency, queue health/backlog age, outcome + clarify-resolution rates.
function AnalyticsPanel({ refreshKey = 0 }) {
  const [result, setResult] = useState(null); // { state, data? }
  const [loading, setLoading] = useState(true);

  // Re-fetches when the page's Refresh bumps refreshKey, so the analytics row
  // can't disagree with the freshly-reloaded status board on shared metrics.
  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .getReviewAnalytics({ type: REVIEW_TYPE, examType: EXAM_TYPE, windowDays: 0 })
      .then((res) => active && setResult(analyticsResult(res)))
      .catch(() => active && setResult({ state: "error" }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [refreshKey]);

  if (loading) return null;
  if (!result || result.state === "dark") {
    return null; // dark → the analytics surface simply doesn't appear
  }
  if (result.state === "error") {
    return (
      <div className="rs-section">
        <Typography color="error" className="rs-error">Could not load operator analytics.</Typography>
      </div>
    );
  }

  const d = result.data;
  const qh = d.queueHealth || {};
  const tp = d.throughput || {};
  const lat = d.reviewLatency || {};
  const out = d.outcomes || {};
  const cl = d.clarify || {};

  return (
    <>
      <TileRow title="Operator analytics — throughput" hint="Reviewer activity (read-only)">
        <StatTile label="Distinct reviewers" value={tp.distinctReviewers || 0} tone="neutral" />
        <StatTile label="Total votes" value={tp.totalVotes || 0} tone="neutral" />
        <StatTile label="Avg votes / reviewer" value={(tp.avgVotesPerReviewer || 0).toFixed(1)} tone="neutral" />
      </TileRow>

      <TileRow title="Review latency" hint="Ingest → decision, over decided items">
        <StatTile label="Decided items" value={lat.decidedItems || 0} tone="neutral" />
        <StatTile label="Median" value={hrs(lat.medianHours)} tone="approved" />
        <StatTile label="p90" value={hrs(lat.p90Hours)} tone="pending" />
      </TileRow>

      <TileRow title="Backlog health" hint="Age of still-pending items">
        <StatTile label="Pending" value={qh.pending || 0} tone="pending" />
        <StatTile label="Oldest pending" value={hrs(qh.oldestPendingAgeHours)} tone="rejected" />
        <StatTile label="Avg pending age" value={hrs(qh.avgPendingAgeHours)} tone="pending" />
      </TileRow>

      <TileRow title="Outcomes" hint="Accept / reject / unsuitable rates">
        <StatTile label="Accept rate" value={fmtPct(out.acceptRate)} tone="approved" />
        <StatTile label="Reject rate" value={fmtPct(out.rejectRate)} tone="rejected" />
        <StatTile label="Unsuitable (img/tbl)" value={`${out.unsuitableForImage || 0}/${out.unsuitableForTable || 0}`} tone="neutral" />
      </TileRow>

      <TileRow title="Clarify loop" hint="GPT reasoning-clarify resolution">
        <StatTile label="Threads" value={cl.threadsTotal || 0} tone="neutral" />
        <StatTile label="Resolution rate" value={fmtPct(cl.resolutionRate)} tone="approved" />
        <StatTile label="Open" value={cl.threadsOpen || 0} tone="pending" />
      </TileRow>
    </>
  );
}

// Correction console (READ-ONLY). Own flag (correctionConsoleEnabled) → dark-safe.
// Visualizes the STAGED bank-mutation ledger (dup merges / error fixes) with a
// before→proposed diff, generates the owner-run QBank apply export as a downloadable
// file, and shows APPLY as a DISABLED, owner-gated control. Nothing here mutates the
// bank — the real prod apply is Akshay-gated (Q5 ROLE_PUBLISHER).
function CorrectionConsolePanel({ refreshKey = 0 }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .getBankMutationExport(EXAM_TYPE)
      .then((res) => active && setResult(exportResult(res)))
      .catch(() => active && setResult({ state: "error" }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const data = result && result.state === "ok" ? result.data : null;
  const entries = (data && data.entries) || [];

  const download = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `qbank-apply-${data.examType || "export"}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  if (loading) return null;
  if (!result || result.state === "dark") return null; // dark → the console doesn't appear
  if (result.state === "error") {
    return (
      <div className="rs-section">
        <Typography color="error" className="rs-error">Could not load the correction console.</Typography>
      </div>
    );
  }

  return (
    <div className="rs-section rs-console">
      <div className="rs-section-head">
        <Typography variant="subtitle1" className="rs-section-title">Correction console</Typography>
        <Typography variant="caption" className="rs-section-hint">
          Staged bank corrections (dup merges / error fixes) — read-only. Apply is owner-gated.
        </Typography>
      </div>

      <div className="rs-console-actions">
        <Button
          size="small"
          variant="outlined"
          startIcon={<GetApp />}
          disabled={entries.length === 0}
          onClick={download}
        >
          Download QBank apply export ({entries.length})
        </Button>
      </div>

      {entries.length === 0 && (
        <Typography className="rs-empty">No staged corrections to apply.</Typography>
      )}

      {entries.map((e, idx) => (
        <Paper key={e.mutationId || idx} className="rs-console-entry" elevation={0}>
          <div className="rs-console-entry-head">
            <Chip size="small" label={e.kind} className="rs-chip-type" />
            <span className="rs-console-qid">question {e.questionId}</span>
            {e.keeperQuestionId && <span className="rs-console-keeper">keep {e.keeperQuestionId}</span>}
          </div>
          <div className="rs-console-diff">
            <div className="rs-console-col">
              <Typography variant="caption" className="rs-console-diff-label">before</Typography>
              <pre className="rs-console-snap">{JSON.stringify(e.before || {}, null, 2)}</pre>
            </div>
            <div className="rs-console-col">
              <Typography variant="caption" className="rs-console-diff-label">proposed</Typography>
              <pre className="rs-console-snap">
                {e.after
                  ? JSON.stringify(e.after, null, 2)
                  : e.kind === "RETIRE_DUPLICATE"
                  ? `retire ${e.questionId}${e.keeperQuestionId ? ` · keep ${e.keeperQuestionId}` : ""}`
                  : "—"}
              </pre>
            </div>
          </div>
          <div className="rs-console-apply">
            <Button
              size="small"
              variant="contained"
              startIcon={<Lock />}
              disabled
              title="Applying to the question bank is owner-gated (ROLE_PUBLISHER). Download the export and apply in QBank."
            >
              Apply (owner-gated)
            </Button>
          </div>
        </Paper>
      ))}
    </div>
  );
}

// Phase-3.3 read-only per-question drill-down. Master-flag gated (dark → the panel
// shows "not enabled", never an error). Reads only — folds a question's review items
// + reasoning + clarify threads + suitability + staged corrections into one view.
// `pageDark` = the status board read came back dark; the drill-down shares its
// backend, so say so up front instead of letting the first lookup be the discovery.
function DrilldownPanel({ pageDark = false }) {
  const [qid, setQid] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { state, data? }

  const lookup = () => {
    const q = qid.trim();
    // Guard busy here (not just on the button): the Enter key also triggers a
    // lookup, and overlapping requests would race — the last to RESOLVE wins,
    // which can render question A's data under question B's id.
    if (busy || !q) return;
    setBusy(true);
    setResult(null);
    api
      .getQuestionDrilldown(q)
      .then((res) => setResult(drilldownResult(res)))
      .catch(() => setResult({ state: "error" }))
      .finally(() => setBusy(false));
  };

  const d = result && result.state === "ok" ? result.data : null;
  const items = (d && d.items) || [];
  const corrections = (d && d.corrections) || [];
  const suit = d && d.suitability;
  const hasSuitFlag = suit && (suit.unsuitableForImage || suit.unsuitableForTable);
  const hasContent = items.length > 0 || corrections.length > 0 || hasSuitFlag;

  return (
    <div className="rs-section rs-drill">
      <div className="rs-section-head">
        <Typography variant="subtitle1" className="rs-section-title">Per-question drill-down</Typography>
        <Typography variant="caption" className="rs-section-hint">
          Every review signal for one question — status, reasoning, clarify threads, suitability, staged corrections
        </Typography>
      </div>
      <div className="rs-drill-search">
        <TextField
          size="small"
          variant="outlined"
          label="Question id"
          value={qid}
          onChange={(e) => setQid(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          helperText="Paste a question id, then Look up"
        />
        <Button
          size="small"
          variant="contained"
          startIcon={<Search />}
          disabled={busy || !qid.trim()}
          onClick={lookup}
        >
          {busy ? "Looking…" : "Look up"}
        </Button>
      </div>

      {/* The board and the drill-down share the master flag but not the type flag,
          so a dark board makes a dark drill-down likely, not certain — heads-up only.
          Suppress while busy: lookup() clears result first, so without !busy the note
          would flash back in during the very lookup that answers it. */}
      {pageDark && !result && !busy && (
        <Typography className="rs-drill-note">
          Queue status above is still dark, so the drill-down is most likely waiting on the
          same backend enablement — a lookup will answer definitively.
        </Typography>
      )}

      {result && result.state === "dark" && (
        <Typography className="rs-empty">Review drill-down is not enabled yet.</Typography>
      )}
      {result && result.state === "error" && (
        <Typography color="error" className="rs-error">Could not load the drill-down.</Typography>
      )}

      {d && (
        <div className="rs-drill-result">
          {hasSuitFlag && (
            <div className="rs-drill-suit">
              {suit.unsuitableForImage && <Chip size="small" label="no image belongs" className="rs-chip-warn" />}
              {suit.unsuitableForTable && <Chip size="small" label="no table belongs" className="rs-chip-warn" />}
            </div>
          )}
          {!hasContent && (
            <Typography className="rs-empty">No review signals for this question.</Typography>
          )}
          {items.map((it) => (
            <Paper key={it.id} className="rs-drill-item" elevation={0}>
              <div className="rs-drill-item-head">
                <Chip size="small" label={it.reviewType} className="rs-chip-type" />
                <Chip size="small" label={it.reviewStatus} className="rs-chip-status" />
                <Chip size="small" label={it.publishStatus} className="rs-chip-status" />
                <span className="rs-drill-votes">
                  ▲{it.approveCount} ▼{it.rejectCount} ★{it.bestCount} · {it.distinctReviewers} reviewer(s)
                </span>
              </div>
              {(it.reasoning || []).map((r, i) => (
                <Typography key={i} variant="body2" className="rs-drill-reason">“{r}”</Typography>
              ))}
              {(it.clarifications || []).map((c, i) => (
                <div key={i} className="rs-drill-clarify">
                  <Typography variant="caption" className="rs-drill-clarify-q">{c.question}</Typography>
                  {c.answer && <Typography variant="body2" className="rs-drill-clarify-a">{c.answer}</Typography>}
                </div>
              ))}
            </Paper>
          ))}
          {corrections.length > 0 && (
            <div className="rs-drill-corrections">
              <Typography variant="subtitle2">Staged corrections</Typography>
              {corrections.map((c) => (
                <Chip
                  key={c.id}
                  size="small"
                  label={`${c.kind} · ${c.status}`}
                  className={c.status === "REVERTED" ? "rs-chip-status" : "rs-chip-warn"}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReviewStatusPage() {
  const role = window.localStorage.getItem("role");
  const canView = role === "ROLE_ADMIN" || role === "ROLE_TUTOR";

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [disabled, setDisabled] = useState(false);
  // Suitability is a SECONDARY, best-effort read behind its own flag: null while
  // the flag is dark (404) or on any error, so it simply hides its row and never
  // blocks the main status board.
  const [suit, setSuit] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .getSuitability(EXAM_TYPE)
      .then((res) => setSuit(Array.isArray(res) ? res : null))
      .catch(() => setSuit(null));
    api
      .getReviewQueueStatus({ type: REVIEW_TYPE, examType: EXAM_TYPE })
      .then((res) => {
        // Flag-gated backend 404s while dark (doGetCall → { status, errMsg }).
        if (res && res.status === 404) {
          setDisabled(true);
          setStatus(null);
          return;
        }
        // Non-200 error object, a network reject (Error, no .status), or an
        // unexpected shape → surface an error, not a misleading zeroed board.
        if (!res || res.status || !res.reviewStatus) {
          setError("Could not load queue status.");
          setStatus(null);
          setDisabled(false); // an error is not the dark state — never show both
          return;
        }
        setDisabled(false);
        setStatus(res);
      })
      .catch(() => {
        // doGetCall resolves (never rejects) today, so this is unreachable — but keep
        // it consistent with the error branch above: an error clears the dark state and
        // the stale board, so the two can never render together if that ever changes.
        setError("Could not load queue status.");
        setDisabled(false);
        setStatus(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  if (!canView) {
    return (
      <Box className="body-cont-over rs-page">
        <Menu />
        <div className="rs-shell">
          <div className="rs-denied">
            <Typography variant="h6">Queue status is available to tutors and admins.</Typography>
          </div>
        </div>
      </Box>
    );
  }

  const rev = status && status.reviewStatus;
  const pub = status && status.publishStatus;
  const total = status ? status.total : 0;
  const decided = rev ? rev.approved + rev.rejected : 0;
  const pct = total > 0 ? Math.round((decided / total) * 100) : 0;

  return (
    <Box className="body-cont-over rs-page">
      <Menu />
      <Backdrop open={loading} style={{ zIndex: 1400, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="rs-shell">
        <div className="rs-header">
          <div>
            <Typography variant="h5">ABSITE image review — queue status</Typography>
            <Typography variant="body2" className="rs-sub">
              Progress of the tutor image-feedback pipeline. Two axes: reviewer FLAG (Pending →
              Approved/Rejected) and admin PUBLISH (Unpublished → Published/Retracted).
            </Typography>
          </div>
          <Button
            size="small"
            variant="outlined"
            className="rs-refresh"
            startIcon={<Refresh />}
            disabled={loading}
            onClick={() => {
              load();
              // Panels fetch on mount with key 0; bumping here (not in load, which
              // also runs on mount) re-fetches them exactly once per Refresh.
              setRefreshKey((k) => k + 1);
            }}
          >
            Refresh
          </Button>
        </div>

        {error && <Typography color="error" className="rs-error">{error}</Typography>}

        {!loading && disabled && (
          <Paper className="rs-dark" elevation={0}>
            <Typography variant="subtitle1" className="rs-dark-title">
              Queue status isn't live on this server yet
            </Typography>
            <Typography variant="body2" className="rs-dark-body">
              Nothing is broken — this dashboard ships ahead of the backend that feeds it.
              It will fill in here once the QLearn-service deploy catches up and the
              review-status flag is on; there's nothing to configure. Hit Refresh to re-check.
            </Typography>
            <Typography variant="body2" component="ul" className="rs-dark-list">
              <li>Overall progress — how much of the ABSITE image review is done</li>
              <li>Reviewer FLAG and admin PUBLISH stage counts (the two-axis pipeline)</li>
              <li>Reviewer activity (the per-question drill-down below has its own flag)</li>
            </Typography>
          </Paper>
        )}

        {!loading && !disabled && status && total === 0 && (
          <Typography className="rs-zero-note">
            Queue status is live, but no {EXAM_TYPE} image candidates have been ingested yet —
            the board will appear here once the pipeline produces candidates.
          </Typography>
        )}

        {/* Suppress the whole board at total===0: "0 of 0 reviewed · 0%" over a wall of
            zero tiles reads as broken, not as scaffold, and fights the zero-note above.
            total IS the bucket sum, so total===0 means every tile is necessarily 0 —
            there is no legitimate live-but-all-zero state this hides. */}
        {!loading && !disabled && status && total > 0 && (
          <>
            <div className="rs-progress">
              <div className="rs-progress-label">
                <span>{decided} of {total} reviewed</span>
                <span>{pct}%</span>
              </div>
              <div className="rs-progress-track">
                <div className="rs-progress-fill" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <TileRow title="Reviewer stage (FLAG)" hint="Stage 1 — tutors flag; not student-visible">
              <StatTile label="Pending" value={rev.pending} tone="pending" />
              <StatTile label="Approved" value={rev.approved} tone="approved" />
              <StatTile label="Rejected" value={rev.rejected} tone="rejected" />
            </TileRow>

            <TileRow title="Publish stage (admin)" hint="Stage 2 — admin publishes; only then student-visible">
              <StatTile label="Unpublished" value={pub.unpublished} tone="pending" />
              <StatTile label="Published" value={pub.published} tone="approved" />
              <StatTile label="Retracted" value={pub.retracted} tone="rejected" />
            </TileRow>

            <TileRow title="Reviewer activity">
              <StatTile label="Distinct reviewers" value={status.distinctReviewers} tone="neutral" />
              <StatTile label="Questions with a best pick" value={status.itemsWithBestPick} tone="neutral" />
              <StatTile label="Total votes" value={status.totalVotes} tone="neutral" />
            </TileRow>

            {Array.isArray(suit) && (
              <TileRow
                title="Marked unsuitable"
                hint="Reviewers excluded these questions from the candidate pipeline (reversible)"
              >
                <StatTile
                  label="No image belongs"
                  value={suit.filter((s) => s.unsuitableForImage).length}
                  tone="rejected"
                />
                <StatTile
                  label="No table belongs"
                  value={suit.filter((s) => s.unsuitableForTable).length}
                  tone="rejected"
                />
                <StatTile label="Questions flagged" value={suit.length} tone="neutral" />
              </TileRow>
            )}
          </>
        )}

        {/* Each independently flag-gated + dark-safe, so they render regardless of the
            queue-status board above (and each other). */}
        <AnalyticsPanel refreshKey={refreshKey} />
        {/* The export read behind the console is ROLE_ADMIN-only server-side; a
            tutor mount-fetch would 403 into a misleading error banner. */}
        {role === "ROLE_ADMIN" && <CorrectionConsolePanel refreshKey={refreshKey} />}
        <DrilldownPanel pageDark={!loading && disabled} />
      </div>
    </Box>
  );
}
